import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ItemStatus } from './models';
import { baseApi } from '@centerpiece/components';

export const item_status = baseApi
  .enhanceEndpoints({ addTagTypes: ['ItemStatuses', 'ItemStatus'] })
  .injectEndpoints({
    endpoints: builder => ({
      getItemStatuses: builder.query<ItemStatus[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'item_status',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ItemStatus' as const,
                  id,
                })),
                'ItemStatuses',
              ]
            : ['ItemStatuses'],
      }),
      getItemStatus: builder.query<ItemStatus, string>({
        query: id => ({
          url: `item_status/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'ItemStatus', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createItemStatus: builder.mutation({
        query: request => ({
          url: 'item_status',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ItemStatuses'],
      }),
      updateItemStatus: builder.mutation<ItemStatus, ItemStatus>({
        query: request => ({
          url: `item_status/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ItemStatus', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            item_status.util.updateQueryData(
              'getItemStatus',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(item_status.util.invalidateTags(['ItemStatuses']))
             */
            dispatch(item_status.util.invalidateTags(['ItemStatuses']));
          }
        },
      }),
      deleteItemStatus: builder.mutation<ItemStatus, number>({
        query: id => ({
          url: `item_status/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ItemStatus' as const, arg }, 'ItemStatuses']
            : ['ItemStatuses'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetItemStatusesQuery,
  useGetItemStatusQuery,
  useCreateItemStatusMutation,
  useUpdateItemStatusMutation,
  useDeleteItemStatusMutation,
  usePrefetch: useItemStatusesPrefetch,
} = item_status;
const selectItemStatusesRoot = item_status.endpoints.getItemStatuses.select({});
export const selectItemStatuses = createSelector(
  selectItemStatusesRoot,
  item_status => item_status.data || []
);
